import {
  mergeTyped,
  useDataStream,
  useNavigator,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { mapTo } from 'rxjs/operators';
import { createCompany } from '../../../api/fetch-functions/company';
import { CompanyAPIRequestBodyType } from '../../../api/types/company/api-request-body';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import { useToken, useUserDetail } from '../../../global-state/auth-state';
import { fetchCompanyRelatedList } from '../../../global-state/database/combined';
import { fetchCompanyListOf } from '../../../global-state/database/company-list';
import { koumutenId$ } from '../../../global-state/router-id';
import { routes } from '../../../routing/routes';
import { ICompanyType } from '../../../types/record/company';
import { CreateNewCompanyDialogView } from './create-new-company-dialog-view';

const formInputStateConfig: FormInputStateConfigType<CompanyAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.companyName,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.companyName,
  required: true,
  validationErrorContentMapFn: content =>
    content.name !== undefined ? content.name.toArray() : []
};

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
}

export const CreateNewCompanyDialog: FC<Props> = memo(
  ({ open, onClose }: Readonly<Props>) => {
    const token = useToken();
    const { name } = useUserDetail();

    /* state */

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<ICompanyType, CompanyAPIRequestBodyType>();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      companyName$,
      companyNameErrors$,
      companyNameIsValid$,
      companyNameOnInput
    ] = useFormInputState<ICompanyType, CompanyAPIRequestBodyType>(
      open,
      fetchResult,
      formInputStateConfig
    );

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(submitOnClick$.pipe(mapTo(false)), companyNameIsValid$)
    );

    /* extract values */

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);

    const koumutenId = useStreamValue(koumutenId$);
    const companyName = useStreamValue(companyName$);
    const companyNameErrors = useStreamValue(companyNameErrors$);

    /* event handler */

    const navigator = useNavigator();

    const closeOnClick = useCallback(
      (reason: '' | 'cancel' | 'ok') => {
        if (reason !== 'ok') {
          onClose(reason);
        } else {
          submitOnClick();
          if (
            token !== undefined &&
            koumutenId !== undefined &&
            companyName !== undefined
          ) {
            createCompany(token)({
              koumutenId,
              name: companyName
            }).then(res => {
              setFetchResult(res);
              if (res.type === 'success') {
                onClose(reason);
                fetchCompanyListOf(koumutenId); // パンくずリストの会社名表示のために必要． TODO: list全体ではなくcompanyIdの要素のみfetchするようにする．
                const companyId = res.body.id;
                fetchCompanyRelatedList(companyId);
                navigator(routes.companyRoot(koumutenId, companyId));
              }
            });
          }
        }
      },
      [
        onClose,
        koumutenId,
        companyName,
        token,
        setFetchResult,
        submitOnClick,
        navigator
      ]
    );

    return (
      <CreateNewCompanyDialogView
        open={open}
        onClose={closeOnClick}
        registererName={name}
        companyName={companyName}
        companyNameErrors={companyNameErrors}
        companyNameOnInput={companyNameOnInput}
        submitButtonEnabled={submitButtonEnabled}
        nonFieldErrors={nonFieldErrors}
      />
    );
  }
);

CreateNewCompanyDialog.displayName = 'CreateNewCompanyDialog';
